import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="hidden flex-col justify-between bg-[#0A2540] p-12 text-white lg:flex">
        <Link to="/" className="font-display text-lg font-bold tracking-tight" data-testid="forgot-home-link">
          MedAdmits
        </Link>
        <div>
          <ShieldCheck className="h-10 w-10 text-white/80" />
          <h2 className="font-display mt-6 text-3xl font-semibold leading-tight tracking-tight">
            Recover your access.
            <br />
            <span className="text-white/60">We'll email you a reset link.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm text-white/70">
            For security, the link expires in 30 minutes and works only once.
            If you don't see the email, check your spam folder.
          </p>
        </div>
        <p className="text-xs text-white/50">© MedAdmits · Private MBBS Council</p>
      </aside>

      <main className="flex items-center justify-center bg-[#F6F8FB] px-6 py-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md rounded-2xl bg-white p-10 shadow-[0_24px_48px_-24px_rgba(10,37,64,0.18)]"
          data-testid="forgot-form"
        >
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0A2540] text-white">
            <Mail className="h-5 w-5" />
          </div>
          <h1 className="font-display mt-4 text-2xl font-semibold tracking-tight text-[#0A2540]">
            Forgot password?
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Enter your account email and we'll send a reset link.
          </p>

          {sent ? (
            <div
              className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
              data-testid="forgot-success"
            >
              <CheckCircle2 className="mb-2 h-5 w-5" />
              If that email is registered with MedAdmits, a reset link has been
              sent. Check your inbox (and spam folder) — the link expires in 30
              minutes.
            </div>
          ) : (
            <>
              <div className="mt-6">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@medadmits.com"
                  className="mt-1.5 h-12"
                  data-testid="forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="mt-6 h-12 w-full bg-[#0A2540] font-semibold text-white hover:bg-[#1E3A5F]"
                data-testid="forgot-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
            </>
          )}

          <p className="mt-6 text-center text-xs text-slate-500">
            Remembered your password?{" "}
            <Link to="/admin/login" className="text-[#0A2540] hover:underline" data-testid="forgot-back-login">
              Back to login
            </Link>
          </p>
        </form>
      </main>
    </div>
  );
}
